import React from 'react';
import { Dimensions } from 'react-native';

import { tokens } from '@opendoor/tokens-studio/dist/bricks-next/tokens/Components';
import { getTokenValue } from '@tamagui/core';
import { Toast as TamaguiToast, ToastProps as TamaguiToastProps } from '@tamagui/toast';

import { AnalyticsProps } from '../../helpers/analytics';
import { getOpacityTokenValue } from '../../helpers/tokens';
import { Box } from '../Box';
import { CloseButton } from '../Button';
import { Text } from '../Text';

const { toast: toastTokens } = tokens;

export type ToastProps = Pick<TamaguiToastProps, 'onOpenChange'> &
  Partial<AnalyticsProps> & {
    /**
     * The message to be displayed in the toast.
     */
    message: string;
    /**
     * The duration for which the toast will be visible (in milliseconds).
     * Optional: If not provided, a default duration of 3000ms will be used.
     */
    duration?: number;
    /**
     * Whether the Toast is open
     */
    isOpen: boolean;
    /**
     * Whether the Toast comes on success
     */
    isSuccess?: boolean;
    /**
     * Whether the Toast has error
     */
    hasError?: boolean;
    /**
     * Callback fired when the Toast is closed.
     */
    onClose?: () => void;
  };

/**
 * Toast component.
 *
 * This component is used to display a brief message or notification to the user.
 * It can be customized with different props, such as the message content, duration,
 * and appearance based on success or error states.
 * @param {string} props.analyticsName - A unique string identifier used for analytics tracking.
 *                                        It helps distinguish between different actions or components
 *                                        when collecting and analyzing user interaction data.
 *                                        For example, if the `analyticsName` is "toast", events related
 *                                        to the close button might be tracked as "toast-close".
 *  To open the Toast byDefault i.e. when you do not need to control its open state, pass isOpen={true}, then the toast will be open by default.
 * @param {(open: boolean) => void} [props.onOpenChange] - Callback fired when the open state changes.
 * @callback onClose
 * @param {boolean} isOpen - Indicates if the Toast is currently open. This should be set to `false` when the Toast is closed.
 *
 * @example
 * // Usage example:
 * const handleToastClose = (isOpen) => {
 *   if (!isOpen) {
 *     console.log("Toast has been closed.");
 *   }
 * };
 *
 * <Toast
 *   message="Operation successful!"
 *   duration={3000}
 *   isOpen={true}
 *   onClose={handleToastClose}
 * />
 *
 * @param {ToastProps} props - The props for the component.
 * @returns {React.JSX.Element} The rendered component.
 */
export const Toast = ({
  message,
  duration = 3000,
  analyticsName,
  trackImpression,
  onOpenChange,
  isOpen,
  isSuccess = false,
  hasError = false,
  onClose,
}: ToastProps) => {
  const { width: screenWidth } = Dimensions.get('window');
  const width = screenWidth * 0.95;

  function handleToastOpenChange(open: boolean) {
    if (!open) {
      onClose?.(); // Call the onClose callback if it's provided
    }
    onOpenChange?.(open); // Call the passed onOpenChange function if provided
  }

  const getBackgroundColor = () => {
    if (hasError) return toastTokens.color.background.error;
    if (isSuccess) return toastTokens.color.background.success;
    return toastTokens.color.background.default;
  };

  return (
    <TamaguiToast
      zIndex={1000}
      open={isOpen}
      onOpenChange={handleToastOpenChange}
      animation={toastTokens.animation.timing.default}
      enterStyle={{
        x: 0,
        y: toastTokens.animation.enterTransition.offset.start,
        opacity: getOpacityTokenValue(toastTokens.animation.opacity.enter.start),
      }}
      exitStyle={{
        x: 0,
        y: -getTokenValue(toastTokens.animation.exitTransition.end),
        opacity: getOpacityTokenValue(toastTokens.animation.opacity.exit.end),
      }}
      duration={duration}
      $largerThanSM={{
        borderRadius: toastTokens.radius.size.m,
        marginVertical: toastTokens.margin.topBottom.m,
        paddingVertical: toastTokens.padding.topBottom.m,
        paddingHorizontal: toastTokens.padding.leftRight.m,
      }}
      $largerThanMD={{
        borderRadius: toastTokens.radius.size.lAndXl,
        marginVertical: toastTokens.margin.topBottom.lAndXl,
        paddingVertical: toastTokens.padding.topBottom.lAndXl,
        paddingHorizontal: toastTokens.padding.leftRight.lAndXl,
      }}
      marginVertical={toastTokens.margin.topBottom.xsAndS}
      paddingVertical={toastTokens.padding.topBottom.xsAndS}
      paddingHorizontal={toastTokens.padding.leftRight.xsAndX}
      borderRadius={toastTokens.radius.size.sAndXs}
      backgroundColor={getBackgroundColor()}
      width={width}
      maxWidth={toastTokens.size.maxWidth}
    >
      <Box
        justifyContent="space-between"
        analyticsName={`${analyticsName}-toast`}
        trackImpression={trackImpression}
        flexDirection="row"
        alignItems="center"
      >
        <Text
          tag="span"
          color={toastTokens.color.content.valueText.default}
          typography={toastTokens.typography.valueText}
        >
          {message}
        </Text>
        <TamaguiToast.Close asChild>
          <CloseButton analyticsName={`${analyticsName}-close`} size="small" variant="inverse" />
        </TamaguiToast.Close>
      </Box>
    </TamaguiToast>
  );
};
